/**
 * Tasks-specific export hooks.
 * Generic export/import/validate hooks are in shared/hooks/useExportImport.ts.
 * This file only contains the tasks-specific export tree hook.
 */

import { useQuery } from '@tanstack/react-query';
import { API_BASE_URL } from '../../../shared/api/config';
import { AUTH_STORAGE_KEYS } from '../../../shared/auth/storageKeys';

const EXPORT_IMPORT_ENDPOINT = '/api/v1/tasks/export-import';

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export interface ExportTreeBoard {
  id: string;
  name: string;
  color: string;
  icon: string;
}

export interface ExportTreeWorkspace {
  id: string;
  name: string;
  icon: string;
  color: string;
  boards: ExportTreeBoard[];
}

/**
 * Hook to fetch the workspace/board tree for the export selector.
 */
export function useExportTree(enabled = false) {
  return useQuery<ExportTreeWorkspace[]>({
    queryKey: ['tasks', 'export-tree'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}${EXPORT_IMPORT_ENDPOINT}/export/tree`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        throw error;
      }

      const result = await response.json();
      return result.data;
    },
    enabled,
    staleTime: 30_000,
  });
}
